import { StrictMode, useCallback, useEffect, useState, type FormEvent } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, Show, SignIn, useAuth, UserButton } from "@clerk/react";
import logo from "@dropdex/brand/color.png";
import { supportedTcgs } from "@dropdex/config";
import { Badge, BrandLogo, Button, Field, Panel, Toggle } from "@dropdex/ui";
import "@dropdex/ui/styles.css";
import "./admin.css";

const nav = ["Overview", "Content", "Publish", "TCGs", "Alert sources", "In-Store Restocks", "Webhook logs", "Users", "Notifications", "Chat moderation", "Audit history"] as const;
type AdminTab = (typeof nav)[number];
type ContentKind = "flip" | "deal" | "news" | "tcg";
type PublicationStatus = "draft" | "published";
type SaveState = "idle" | "saving" | "success" | "error";

interface ContentFormState {
  kind: ContentKind;
  title: string;
  summary: string;
  retailer: string;
  price: string;
  originalPrice: string;
  resaleValue: string;
  productUrl: string;
  imageUrl: string;
  tcg: string;
  alertStatus: string;
  publicationStatus: PublicationStatus;
  featured: boolean;
}

interface AdminContentItem {
  id: string;
  kind: ContentKind;
  title: string;
  summary: string;
  imageUrl?: string;
  productUrl?: string;
  retailer?: string;
  price?: number;
  originalPrice?: number;
  resaleValue?: number;
  profit?: number;
  profitPercent?: number;
  featured?: boolean;
  source: "manual" | "automated";
  tcg?: string;
  status?: string;
  publicationStatus: PublicationStatus;
  publishedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface AdminTcg {
  id: string;
  name: string;
  shortName: string;
  enabled: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

const fallbackTcgs: AdminTcg[] = supportedTcgs.map((tcg, index) => ({ ...tcg, enabled: true, sortOrder: (index + 1) * 10 }));

interface AdminAlertSource {
  id: string;
  name: string;
  slug: string;
  tcgId: string;
  retailer: string | null;
  region: string | null;
  webhookUrl: string;
  enabled: boolean;
  lastSeenAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

interface RestockProvider {
  id: string;
  name: string;
  slug: string;
  retailer: string | null;
  region: string | null;
  endpoint: string;
  webhookUrl?: string;
  enabled: boolean;
  lastSeenAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  rotatedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface RestockProviderForm {
  name: string;
  slug: string;
  retailer: string;
  region: string;
  enabled: boolean;
}

const emptyRestockProviderForm: RestockProviderForm = {
  name: "",
  slug: "",
  retailer: "",
  region: "US",
  enabled: true
};

interface AlertSourceFormState {
  name: string;
  slug: string;
  tcgId: string;
  retailer: string;
  region: string;
  enabled: boolean;
}

const emptyAlertSourceForm: AlertSourceFormState = {
  name: "",
  slug: "",
  tcgId: "pokemon",
  retailer: "",
  region: "",
  enabled: true
};

const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, "");
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const emptyForm: ContentFormState = {
  kind: "deal",
  title: "",
  summary: "",
  retailer: "",
  price: "",
  originalPrice: "",
  resaleValue: "",
  productUrl: "",
  imageUrl: "",
  tcg: "pokemon",
  alertStatus: "update",
  publicationStatus: "draft",
  featured: false
};

function optionalNumber(value: string) {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function numberInput(value?: number) {
  return value === undefined ? "" : String(value);
}

function formatTimestamp(value: string | null) {
  if (!value) return "recently";
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value)
    ? `${value.replace(" ", "T")}Z`
    : value;
  return new Date(normalized).toLocaleString();
}

function formFromItem(item: AdminContentItem): ContentFormState {
  return {
    kind: item.kind,
    title: item.title,
    summary: item.summary,
    retailer: item.retailer || "",
    price: numberInput(item.price),
    originalPrice: numberInput(item.originalPrice),
    resaleValue: numberInput(item.resaleValue),
    productUrl: item.productUrl || "",
    imageUrl: item.imageUrl || "",
    tcg: item.tcg || "pokemon",
    alertStatus: item.status || "update",
    publicationStatus: item.publicationStatus,
    featured: item.featured === true
  };
}

function payloadFromForm(form: ContentFormState, publicationStatus = form.publicationStatus) {
  const price = optionalNumber(form.price);
  const resaleValue = optionalNumber(form.resaleValue);
  const calculatedProfit = form.kind === "flip" && price !== undefined && resaleValue !== undefined
    ? Number((resaleValue - price).toFixed(2))
    : undefined;
  const calculatedProfitPercent = calculatedProfit !== undefined && price && price > 0
    ? Number(((calculatedProfit / price) * 100).toFixed(1))
    : undefined;
  return {
    kind: form.kind,
    title: form.title.trim(),
    summary: form.summary.trim(),
    retailer: form.retailer.trim() || undefined,
    price,
    originalPrice: optionalNumber(form.originalPrice),
    resaleValue,
    profit: calculatedProfit,
    profitPercent: calculatedProfitPercent,
    productUrl: form.productUrl.trim() || undefined,
    imageUrl: form.imageUrl.trim() || undefined,
    tcg: form.kind === "tcg" ? form.tcg : undefined,
    status: form.kind === "tcg" ? form.alertStatus : undefined,
    featured: form.featured,
    publishNow: publicationStatus === "published"
  };
}

function ImageUploader({ imageUrl, onUploaded }: { imageUrl: string; onUploaded: (assetUrl: string) => void }) {
  const { getToken } = useAuth();
  const [state, setState] = useState<"idle" | "uploading" | "error">("idle");
  const [message, setMessage] = useState("");

  async function upload(file?: File) {
    if (!file) return;
    try {
      setState("uploading");
      setMessage("");
      if (file.size > 8 * 1024 * 1024) throw new Error("Images must be 8 MB or smaller.");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const body = new FormData();
      body.set("file", file);
      const response = await fetch(`${apiUrl}/api/v1/admin/media`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}` },
        body
      });
      const result = await response.json() as { data?: { assetUrl?: string }; error?: { message?: string } };
      if (!response.ok || !result.data?.assetUrl) throw new Error(result.error?.message || "The image could not be uploaded.");
      onUploaded(result.data.assetUrl);
      setState("idle");
      setMessage("Image uploaded to DropDex media storage.");
    } catch (cause) {
      setState("error");
      setMessage(cause instanceof Error ? cause.message : "The image could not be uploaded.");
    }
  }

  return <div className="image-uploader">
    <div className="image-preview">{imageUrl ? <img src={imageUrl} alt="Post preview" /> : <div><span>ϟ</span><small>No image selected</small></div>}</div>
    <div className="image-upload-controls">
      <strong>Upload an image</strong>
      <p>JPG, PNG, WebP, GIF, or AVIF · Maximum 8 MB</p>
      <label className={state === "uploading" ? "upload-button is-uploading" : "upload-button"}><input type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/avif" disabled={state === "uploading"} onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; void upload(file); }} /><span>{state === "uploading" ? "Uploading to R2…" : imageUrl ? "Replace image" : "Choose image"}</span></label>
      {imageUrl && <button type="button" className="remove-image" disabled={state === "uploading"} onClick={() => { onUploaded(""); setMessage("Image removed from this post."); }}>Remove image</button>}
      {message && <small className={state === "error" ? "upload-error" : "upload-success"}>{message}</small>}
    </div>
  </div>;
}

function ContentFields({ form, update, tcgs, editing = false }: {
  form: ContentFormState;
  update: <K extends keyof ContentFormState>(key: K, value: ContentFormState[K]) => void;
  tcgs: AdminTcg[];
  editing?: boolean;
}) {
  const selectableTcgs = tcgs.filter((tcg) => tcg.enabled || tcg.id === form.tcg);
  return <div className="admin-form">
    <div className="form-columns">
      <Field label="Content type"><select value={form.kind} onChange={(event) => update("kind", event.target.value as ContentKind)}><option value="flip">Flip — manual</option><option value="deal">Deal — manual</option><option value="news">News — manual</option><option value="tcg">TCG update — manual</option></select></Field>
      {editing && <Field label="Publication status"><select value={form.publicationStatus} onChange={(event) => update("publicationStatus", event.target.value as PublicationStatus)}><option value="draft">Draft — members cannot see it</option><option value="published">Published — visible to members</option></select></Field>}
    </div>
    {form.kind === "tcg" && <div className="form-columns"><Field label="Trading card game"><select value={form.tcg} onChange={(event) => update("tcg", event.target.value)}>{selectableTcgs.map((tcg) => <option value={tcg.id} key={tcg.id}>{tcg.name}</option>)}</select></Field><Field label="Alert status"><select value={form.alertStatus} onChange={(event) => update("alertStatus", event.target.value)}><option value="in-stock">In stock</option><option value="restocked">Restocked</option><option value="preorder">Preorder</option><option value="sold-out">Sold out</option><option value="update">Manual update</option></select></Field></div>}
    <Field label="Title"><input value={form.title} onChange={(event) => update("title", event.target.value)} placeholder="Enter a clear member-facing title" /></Field>
    <Field label="Summary"><textarea value={form.summary} onChange={(event) => update("summary", event.target.value)} rows={4} placeholder="Write the details members need" /></Field>
    <div className="form-columns"><Field label={form.kind === "news" ? "Source" : "Retailer"}><input value={form.retailer} onChange={(event) => update("retailer", event.target.value)} placeholder="Optional" /></Field>{form.kind !== "news" && <Field label={form.kind === "flip" ? "Retail price" : "Current price"}><input value={form.price} onChange={(event) => update("price", event.target.value)} type="number" min="0" step=".01" placeholder="0.00" /></Field>}</div>
    {form.kind === "deal" && <Field label="Original price"><input value={form.originalPrice} onChange={(event) => update("originalPrice", event.target.value)} type="number" min="0" step=".01" placeholder="0.00" /></Field>}
    {form.kind === "flip" && <Field label="Estimated resale value" hint="Profit and profit percentage are calculated automatically."><input value={form.resaleValue} onChange={(event) => update("resaleValue", event.target.value)} type="number" min="0" step=".01" placeholder="0.00" /></Field>}
    <Field label="Product or article URL"><input value={form.productUrl} onChange={(event) => update("productUrl", event.target.value)} type="url" placeholder="https://…" /></Field>
    <ImageUploader imageUrl={form.imageUrl} onUploaded={(assetUrl) => update("imageUrl", assetUrl)} />
    <Field label="Image URL" hint="Uploads fill this automatically. You can still paste an external image URL."><input value={form.imageUrl} onChange={(event) => update("imageUrl", event.target.value)} type="url" placeholder="https://…" /></Field>
    <div className="feature-control"><div><strong>Featured post</strong><small>Highlight this item for members across the mobile and desktop feeds.</small></div><Toggle checked={form.featured} onChange={(value) => update("featured", value)} label="Feature this post" /></div>
  </div>;
}

async function parseApiResponse(response: Response) {
  if (response.status === 204) return null;
  return response.json() as Promise<{ data?: unknown; error?: { message?: string }; meta?: Record<string, unknown> }>;
}

function slugifyTcg(value: string) {
  return value.trim().toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function useAdminTcgs() {
  const { getToken } = useAuth();
  const [tcgs, setTcgs] = useState<AdminTcg[]>(fallbackTcgs);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const response = await fetch(`${apiUrl}/api/v1/admin/tcgs`, { headers: { authorization: `Bearer ${token}` } });
      const result = await response.json() as { data?: { items?: AdminTcg[] }; error?: { message?: string } };
      if (!response.ok) throw new Error(result.error?.message || "TCGs could not be loaded.");
      setTcgs(result.data?.items || []);
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "TCGs could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [getToken]);
  useEffect(() => { void load(); }, [load]);
  return { tcgs, loading, errorMessage, reload: load };
}

function TcgManager() {
  const { getToken } = useAuth();
  const { tcgs, loading, errorMessage, reload } = useAdminTcgs();
  const [name, setName] = useState("");
  const [shortName, setShortName] = useState("");
  const [id, setId] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [state, setState] = useState<SaveState>("idle");
  const [message, setMessage] = useState("");

  async function createTcg() {
    try {
      setState("saving");
      setMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const response = await fetch(`${apiUrl}/api/v1/admin/tcgs`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ id, name, shortName, enabled })
      });
      const result = await parseApiResponse(response);
      if (!response.ok) throw new Error(result?.error?.message || "The TCG could not be added.");
      setName("");
      setShortName("");
      setId("");
      setSlugTouched(false);
      setEnabled(true);
      setState("success");
      setMessage("TCG added to the DropDex catalog.");
      await reload();
    } catch (cause) {
      setState("error");
      setMessage(cause instanceof Error ? cause.message : "The TCG could not be added.");
    }
  }

  return <div className="admin-stack">
    <Panel title="Add a trading card game" action={<Badge tone="gold">Administrator only</Badge>}>
      <form className="tcg-create-form" onSubmit={(event) => { event.preventDefault(); void createTcg(); }}>
        <div className="form-columns">
          <Field label="Full name"><input required minLength={2} maxLength={80} value={name} onChange={(event) => { const value = event.target.value; setName(value); if (!slugTouched) setId(slugifyTcg(value)); }} placeholder="Flesh and Blood" /></Field>
          <Field label="Short name"><input required minLength={2} maxLength={40} value={shortName} onChange={(event) => setShortName(event.target.value)} placeholder="Flesh & Blood" /></Field>
        </div>
        <Field label="URL slug" hint="Permanent lowercase identifier used by feeds and notifications, for example: flesh-and-blood"><input required pattern="[a-z0-9]+(-[a-z0-9]+)*" value={id} onChange={(event) => { setSlugTouched(true); setId(event.target.value.toLowerCase()); }} placeholder="flesh-and-blood" /></Field>
        <div className="feature-control"><div><strong>Available to members</strong><small>Include this TCG in publishing forms, alert-source routing, and product configuration.</small></div><Toggle checked={enabled} onChange={setEnabled} label="Enable this TCG" /></div>
        <div className="form-actions"><Button type="submit" disabled={state === "saving"}>{state === "saving" ? "Adding…" : "Add TCG"}</Button></div>
        {message && <p className={state === "error" ? "error-note" : "success-note"}>{state === "error" ? "!" : "✓"} {message}</p>}
      </form>
    </Panel>
    <Panel title="TCG catalog" action={<Badge tone="green">{tcgs.filter((tcg) => tcg.enabled).length} enabled</Badge>}>
      {errorMessage && <p className="error-note content-message">! {errorMessage}</p>}
      {loading ? <p className="loading-content">Loading TCGs…</p> : <div className="tcg-list">{tcgs.map((tcg) => <article className="tcg-row" key={tcg.id}><div className="tcg-order">{tcg.sortOrder}</div><div><h3>{tcg.name}</h3><p>{tcg.shortName} · <code>{tcg.id}</code></p></div><Badge tone={tcg.enabled ? "green" : "muted"}>{tcg.enabled ? "Enabled" : "Disabled"}</Badge></article>)}</div>}
    </Panel>
  </div>;
}

function Publisher() {
  const { getToken } = useAuth();
  const { tcgs } = useAdminTcgs();
  const [form, setForm] = useState<ContentFormState>(emptyForm);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [message, setMessage] = useState("");

  const update = <K extends keyof ContentFormState>(key: K, value: ContentFormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setSaveState("idle");
    setMessage("");
  };

  async function submit(publicationStatus: PublicationStatus) {
    try {
      setSaveState("saving");
      setMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const response = await fetch(`${apiUrl}/api/v1/admin/content`, {
        method: "POST",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json", "idempotency-key": crypto.randomUUID() },
        body: JSON.stringify(payloadFromForm(form, publicationStatus))
      });
      const result = await parseApiResponse(response) as { data?: { id?: string }; error?: { message?: string }; meta?: { persisted?: string } } | null;
      if (!response.ok) throw new Error(result?.error?.message || "The post could not be saved.");
      setSaveState("success");
      setMessage(`${form.kind.toUpperCase()} ${publicationStatus === "published" ? "published" : "saved as a draft"} in ${result?.meta?.persisted || "the API"}. ID: ${result?.data?.id}`);
      setForm((current) => ({ ...emptyForm, kind: current.kind }));
    } catch (cause) {
      setSaveState("error");
      setMessage(cause instanceof Error ? cause.message : "The post could not be saved.");
    }
  }

  return <div className="admin-stack">
    <Panel title="Manual content publisher" action={<Badge tone="gold">Administrator only</Badge>}>
      <ContentFields form={form} update={update} tcgs={tcgs} />
      <div className="form-actions"><Button variant="ghost" disabled={saveState === "saving"} onClick={() => void submit("draft")}>Save draft</Button><Button disabled={saveState === "saving"} onClick={() => void submit("published")}>{saveState === "saving" ? "Saving…" : "Publish now"}</Button></div>
      {message && <p className={saveState === "error" ? "error-note form-message" : "success-note form-message"}>{saveState === "error" ? "!" : "✓"} {message}</p>}
    </Panel>
    <Panel title="Publishing model"><div className="model-grid"><article><Badge tone="gold">Manual</Badge><h3>Flips, Deals, and News</h3><p>Authorized staff posts are stored in D1 and published newest first. Deals and News have no filter system.</p></article><article><Badge tone="green">Automated + manual</Badge><h3>TCG alerts</h3><p>Manual TCG updates use the same content and alerts tables as automated monitor events.</p></article></div></Panel>
  </div>;
}

function ContentEditor({ item, tcgs, onCancel, onSaved }: { item: AdminContentItem; tcgs: AdminTcg[]; onCancel: () => void; onSaved: () => void }) {
  const { getToken } = useAuth();
  const [form, setForm] = useState<ContentFormState>(() => formFromItem(item));
  const [state, setState] = useState<SaveState>("idle");
  const [message, setMessage] = useState("");
  const update = <K extends keyof ContentFormState>(key: K, value: ContentFormState[K]) => setForm((current) => ({ ...current, [key]: value }));

  async function save() {
    try {
      setState("saving");
      setMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const response = await fetch(`${apiUrl}/api/v1/admin/content/${encodeURIComponent(item.id)}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify(payloadFromForm(form))
      });
      const result = await parseApiResponse(response);
      if (!response.ok) throw new Error(result?.error?.message || "The changes could not be saved.");
      setState("success");
      setMessage("Changes saved in D1.");
      onSaved();
    } catch (cause) {
      setState("error");
      setMessage(cause instanceof Error ? cause.message : "The changes could not be saved.");
    }
  }

  return <Panel title={`Edit: ${item.title}`} action={<Button variant="ghost" onClick={onCancel}>Close editor</Button>}>
    <ContentFields form={form} update={update} tcgs={tcgs} editing />
    <div className="form-actions"><Button variant="ghost" onClick={onCancel}>Cancel</Button><Button disabled={state === "saving"} onClick={() => void save()}>{state === "saving" ? "Saving…" : "Save changes"}</Button></div>
    {message && <p className={state === "error" ? "error-note form-message" : "success-note form-message"}>{state === "error" ? "!" : "✓"} {message}</p>}
  </Panel>;
}

function ContentManager() {
  const { getToken } = useAuth();
  const { tcgs } = useAdminTcgs();
  const [items, setItems] = useState<AdminContentItem[]>([]);
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [editing, setEditing] = useState<AdminContentItem | null>(null);

  const load = useCallback(async (cursor?: string, append = false) => {
    try {
      setLoading(true);
      setErrorMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const query = new URLSearchParams();
      if (kind !== "all") query.set("kind", kind);
      if (status !== "all") query.set("status", status);
      if (search) query.set("search", search);
      if (cursor) query.set("cursor", cursor);
      const response = await fetch(`${apiUrl}/api/v1/admin/content?${query}`, { headers: { authorization: `Bearer ${token}` } });
      const result = await response.json() as { data?: { items?: AdminContentItem[]; pageInfo?: { endCursor: string | null } }; error?: { message?: string } };
      if (!response.ok) throw new Error(result.error?.message || "Content could not be loaded.");
      const nextItems = result.data?.items || [];
      setItems((current) => append ? [...current, ...nextItems] : nextItems);
      setNextCursor(result.data?.pageInfo?.endCursor || null);
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "Content could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [getToken, kind, search, status]);

  useEffect(() => { void load(); }, [load]);

  async function updateItem(item: AdminContentItem, changes: Partial<ContentFormState>) {
    try {
      setBusyId(item.id);
      setErrorMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const form = { ...formFromItem(item), ...changes };
      const response = await fetch(`${apiUrl}/api/v1/admin/content/${encodeURIComponent(item.id)}`, {
        method: "PUT",
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify(payloadFromForm(form))
      });
      const result = await parseApiResponse(response);
      if (!response.ok) throw new Error(result?.error?.message || "The content item could not be updated.");
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The content item could not be updated.");
    } finally {
      setBusyId(null);
    }
  }

  async function deleteItem(item: AdminContentItem) {
    if (!window.confirm(`Delete “${item.title}”? It will immediately disappear from member feeds.`)) return;
    try {
      setBusyId(item.id);
      setErrorMessage("");
      const token = await getToken();
      if (!token || !apiUrl) throw new Error("The admin API is not configured.");
      const response = await fetch(`${apiUrl}/api/v1/admin/content/${encodeURIComponent(item.id)}`, { method: "DELETE", headers: { authorization: `Bearer ${token}` } });
      const result = await parseApiResponse(response);
      if (!response.ok) throw new Error(result?.error?.message || "The content item could not be deleted.");
      if (editing?.id === item.id) setEditing(null);
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The content item could not be deleted.");
    } finally {
      setBusyId(null);
    }
  }

  return <div className="admin-stack">
    {editing && <ContentEditor item={editing} tcgs={tcgs} onCancel={() => setEditing(null)} onSaved={() => void load()} />}
    <Panel title="Content library" action={<Badge tone="green">D1 connected</Badge>}>
      <div className="content-toolbar">
        <form className="content-search" onSubmit={(event) => { event.preventDefault(); setSearch(searchInput.trim()); }}><input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search title, summary, or retailer" /><Button type="submit" variant="ghost">Search</Button></form>
        <select aria-label="Filter by type" value={kind} onChange={(event) => setKind(event.target.value)}><option value="all">All types</option><option value="flip">Flips</option><option value="deal">Deals</option><option value="news">News</option><option value="tcg">TCG</option></select>
        <select aria-label="Filter by status" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All statuses</option><option value="published">Published</option><option value="draft">Drafts</option></select>
        <Button variant="ghost" onClick={() => void load()}>Refresh</Button>
      </div>
      {errorMessage && <p className="error-note content-message">! {errorMessage}</p>}
      <div className="content-summary"><strong>{items.length}</strong><span>posts loaded</span><small>Deleted posts are retained only in the audit trail.</small></div>
      <div className="content-list">
        {!loading && items.length === 0 && <div className="empty-content"><strong>No content found</strong><p>Adjust the filters or create a new post from Publish.</p></div>}
        {items.map((item) => {
          const busy = busyId === item.id;
          return <article className={item.featured ? "content-row is-featured" : "content-row"} key={item.id}>
            <div className="content-thumb">{item.imageUrl ? <img src={item.imageUrl} alt="" /> : <span>ϟ</span>}</div>
            <div className="content-copy"><div className="content-badges"><Badge tone={item.publicationStatus === "published" ? "green" : "muted"}>{item.publicationStatus}</Badge><Badge tone="blue">{item.kind}</Badge>{item.featured && <Badge tone="gold">Featured</Badge>}{item.source === "automated" && <Badge tone="muted">Automated</Badge>}</div><h3>{item.title}</h3><p>{item.summary}</p><small>{item.retailer || "No retailer/source"} · Updated {formatTimestamp(item.updatedAt)}</small></div>
            <div className="content-actions"><Button variant="ghost" disabled={busy} onClick={() => setEditing(item)}>Edit</Button><Button variant="ghost" disabled={busy} onClick={() => void updateItem(item, { featured: !item.featured })}>{item.featured ? "Unfeature" : "Feature"}</Button><Button variant={item.publicationStatus === "published" ? "ghost" : "green"} disabled={busy} onClick={() => void updateItem(item, { publicationStatus: item.publicationStatus === "published" ? "draft" : "published" })}>{item.publicationStatus === "published" ? "Move to draft" : "Publish"}</Button><button className="danger-button" disabled={busy} onClick={() => void deleteItem(item)}>Delete</button></div>
          </article>;
        })}
      </div>
      {loading && <p className="loading-content">Loading content…</p>}
      {nextCursor && !loading && <div className="load-more"><Button variant="ghost" onClick={() => void load(nextCursor, true)}>Load more</Button></div>}
    </Panel>
  </div>;
}

function AlertSources() {
  const { getToken } = useAuth();
  const { tcgs } = useAdminTcgs();
  const [items, setItems] = useState<AdminAlertSource[]>([]);
  const [form, setForm] = useState<AlertSourceFormState>(emptyAlertSourceForm);
  const [editing, setEditing] = useState<AdminAlertSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const request = useCallback(async (path: string, init?: RequestInit) => {
    const token = await getToken();
    if (!token || !apiUrl) throw new Error("The admin API is not configured.");
    const response = await fetch(`${apiUrl}${path}`, {
      ...init,
      headers: { authorization: `Bearer ${token}`, ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers }
    });
    const result = await parseApiResponse(response);
    if (!response.ok) throw new Error(result?.error?.message || "The alert source request failed.");
    return result;
  }, [getToken]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const result = await request("/api/v1/admin/alert-sources");
      setItems((result?.data || []) as AdminAlertSource[]);
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "Alert sources could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { void load(); }, [load]);

  function update<K extends keyof AlertSourceFormState>(key: K, value: AlertSourceFormState[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function resetForm() {
    setEditing(null);
    setForm(emptyAlertSourceForm);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      setBusyId(editing?.id || "new");
      setErrorMessage("");
      setMessage("");
      const payload = {
        name: form.name.trim(), slug: form.slug.trim(), tcgId: form.tcgId,
        retailer: form.retailer.trim() || undefined, region: form.region.trim() || undefined,
        enabled: form.enabled
      };
      await request(editing ? `/api/v1/admin/alert-sources/${encodeURIComponent(editing.id)}` : "/api/v1/admin/alert-sources", {
        method: editing ? "PUT" : "POST", body: JSON.stringify(payload)
      });
      setMessage(editing ? "Alert source updated." : "Alert source created.");
      resetForm();
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The alert source could not be saved.");
    } finally {
      setBusyId(null);
    }
  }

  function beginEdit(item: AdminAlertSource) {
    setEditing(item);
    setForm({ name: item.name, slug: item.slug, tcgId: item.tcgId, retailer: item.retailer || "", region: item.region || "", enabled: item.enabled });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function updateEnabled(item: AdminAlertSource) {
    try {
      setBusyId(item.id);
      await request(`/api/v1/admin/alert-sources/${encodeURIComponent(item.id)}`, {
        method: "PUT",
        body: JSON.stringify({ name: item.name, slug: item.slug, tcgId: item.tcgId, retailer: item.retailer || undefined, region: item.region || undefined, enabled: !item.enabled })
      });
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The source could not be updated.");
    } finally { setBusyId(null); }
  }

  async function rotate(item: AdminAlertSource) {
    if (!window.confirm(`Rotate the webhook token for “${item.name}”? The current provider URL will immediately stop working.`)) return;
    try {
      setBusyId(item.id);
      await request(`/api/v1/admin/alert-sources/${encodeURIComponent(item.id)}/rotate-token`, { method: "POST" });
      setMessage("Webhook token rotated. Copy the new URL into the provider dashboard.");
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The webhook token could not be rotated.");
    } finally { setBusyId(null); }
  }

  async function remove(item: AdminAlertSource) {
    if (!window.confirm(`Delete “${item.name}”? Its webhook URL will stop accepting alerts.`)) return;
    try {
      setBusyId(item.id);
      await request(`/api/v1/admin/alert-sources/${encodeURIComponent(item.id)}`, { method: "DELETE" });
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The source could not be deleted.");
    } finally { setBusyId(null); }
  }

  async function copyWebhook(item: AdminAlertSource) {
    try {
      await navigator.clipboard.writeText(item.webhookUrl);
      setMessage(`${item.name} webhook copied.`);
    } catch { setErrorMessage("Copy failed. Select the webhook URL and copy it manually."); }
  }

  return <div className="admin-stack">
    <Panel title={editing ? `Edit source: ${editing.name}` : "Add alert source"} action={<Badge tone="gold">{tcgs.filter((tcg) => tcg.enabled).length} enabled TCGs</Badge>}>
      <div className="source-intro"><p>Create one routing source for each provider feed. The generated URL securely routes alerts into the selected TCG automatically.</p></div>
      <form className="source-form" onSubmit={(event) => void save(event)}>
        <div className="form-columns"><Field label="Source name"><input required value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Target US Pokémon" /></Field><Field label="URL slug"><input required value={form.slug} onChange={(event) => update("slug", event.target.value)} placeholder="target-us-pokemon" /></Field></div>
        <div className="form-columns"><Field label="Trading card game"><select value={form.tcgId} onChange={(event) => update("tcgId", event.target.value)}>{tcgs.filter((tcg) => tcg.enabled || tcg.id === form.tcgId).map((tcg) => <option value={tcg.id} key={tcg.id}>{tcg.name}</option>)}</select></Field><Field label="Retailer"><input value={form.retailer} onChange={(event) => update("retailer", event.target.value)} placeholder="Target" /></Field></div>
        <div className="form-columns"><Field label="Region"><input value={form.region} onChange={(event) => update("region", event.target.value)} placeholder="US" /></Field><Toggle checked={form.enabled} onChange={(value) => update("enabled", value)} label="Accept incoming alerts" /></div>
        <div className="source-form-actions">{editing && <Button type="button" variant="ghost" onClick={resetForm}>Cancel</Button>}<Button type="submit" disabled={busyId === (editing?.id || "new")}>{busyId ? "Saving…" : editing ? "Save source" : "Create source"}</Button></div>
      </form>
    </Panel>
    <Panel title="Provider webhook routing" action={<Button variant="ghost" onClick={() => void load()}>Refresh</Button>}>
      {message && <p className="success-note content-message">✓ {message}</p>}
      {errorMessage && <p className="error-note content-message">! {errorMessage}</p>}
      {loading && <p className="loading-content">Loading alert sources…</p>}
      {!loading && items.length === 0 && <div className="empty-content"><strong>No alert sources found</strong><p>Create a source above to generate its provider webhook URL.</p></div>}
      <div className="source-list">{items.map((item) => {
        const tcg = tcgs.find((entry) => entry.id === item.tcgId);
        const busy = busyId === item.id;
        return <article className="source-card" key={item.id}>
          <div className="source-card-header"><div><div className="content-badges"><Badge tone="blue">{tcg?.name || item.tcgId}</Badge><Badge tone={item.enabled ? "green" : "muted"}>{item.enabled ? "Enabled" : "Disabled"}</Badge></div><h3>{item.name}</h3><p>{[item.retailer, item.region].filter(Boolean).join(" · ") || "Any retailer or region"}</p></div><small>Updated {formatTimestamp(item.updatedAt)}</small></div>
          <label className="source-url"><span>Provider webhook URL</span><input readOnly value={item.webhookUrl} onFocus={(event) => event.currentTarget.select()} /></label>
          <div className="source-actions"><Button variant="ghost" disabled={busy} onClick={() => void copyWebhook(item)}>Copy webhook</Button><Button variant="ghost" disabled={busy} onClick={() => void rotate(item)}>Rotate token</Button><Button variant="ghost" disabled={busy} onClick={() => beginEdit(item)}>Edit</Button><Button variant={item.enabled ? "ghost" : "green"} disabled={busy} onClick={() => void updateEnabled(item)}>{item.enabled ? "Disable" : "Enable"}</Button><button className="danger-button" disabled={busy} onClick={() => void remove(item)}>Delete</button></div>
          <div className="source-status"><span>Last received: {item.lastSeenAt ? formatTimestamp(item.lastSeenAt) : "Never"}</span><span>Status: {item.lastStatus || "Waiting"}</span>{item.lastError && <span className="source-error">{item.lastError}</span>}</div>
        </article>;
      })}</div>
    </Panel>
  </div>;
}


function RestockProviders() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<RestockProvider[]>([]);
  const [form, setForm] = useState<RestockProviderForm>(emptyRestockProviderForm);
  const [editing, setEditing] = useState<RestockProvider | null>(null);
  const [credential, setCredential] = useState<{ name: string; webhookUrl: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const request = useCallback(async (path: string, init?: RequestInit) => {
    const token = await getToken();
    if (!token || !apiUrl) throw new Error("The admin API is not configured.");
    const response = await fetch(`${apiUrl}${path}`, {
      ...init,
      headers: { authorization: `Bearer ${token}`, ...(init?.body ? { "content-type": "application/json" } : {}), ...init?.headers }
    });
    const result = await parseApiResponse(response);
    if (!response.ok) throw new Error(result?.error?.message || "The restock provider request failed.");
    return result;
  }, [getToken]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const result = await request("/api/v1/admin/in-store-restock-providers");
      setItems((result?.data || []) as RestockProvider[]);
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "Restock providers could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => { void load(); }, [load]);

  function update<K extends keyof RestockProviderForm>(key: K, value: RestockProviderForm[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function resetForm() {
    setEditing(null);
    setForm(emptyRestockProviderForm);
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      setBusyId(editing?.id || "new");
      setErrorMessage("");
      setMessage("");
      const result = await request(editing ? `/api/v1/admin/in-store-restock-providers/${encodeURIComponent(editing.id)}` : "/api/v1/admin/in-store-restock-providers", {
        method: editing ? "PUT" : "POST",
        body: JSON.stringify({ ...form, name: form.name.trim(), slug: form.slug.trim(), retailer: form.retailer.trim() || undefined, region: form.region.trim() || undefined })
      });
      const saved = result?.data as RestockProvider | undefined;
      if (saved?.webhookUrl) setCredential({ name: saved.name, webhookUrl: saved.webhookUrl });
      setMessage(editing ? "Provider updated." : "Provider created. Copy the webhook now; its token will not be shown again.");
      resetForm();
      await load();
    } catch (cause) {
      setErrorMessage(cause instanceof Error ? cause.message : "The provider could not be saved.");
    } finally { setBusyId(null); }
  }

  function beginEdit(item: RestockProvider) {
    setEditing(item);
    setForm({ name: item.name, slug: item.slug, retailer: item.retailer || "", region: item.region || "", enabled: item.enabled });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function toggle(item: RestockProvider) {
    try {
      setBusyId(item.id);
      await request(`/api/v1/admin/in-store-restock-providers/${encodeURIComponent(item.id)}`, {
        method: "PUT",
        body: JSON.stringify({ name: item.name, slug: item.slug, retailer: item.retailer || undefined, region: item.region || undefined, enabled: !item.enabled })
      });
      await load();
    } catch (cause) { setErrorMessage(cause instanceof Error ? cause.message : "The provider could not be updated."); }
    finally { setBusyId(null); }
  }

  async function rotate(item: RestockProvider) {
    if (!window.confirm(`Rotate the credential for “${item.name}”? Its current webhook will immediately stop working.`)) return;
    try {
      setBusyId(item.id);
      const result = await request(`/api/v1/admin/in-store-restock-providers/${encodeURIComponent(item.id)}/rotate-token`, { method: "POST" });
      const saved = result?.data as RestockProvider | undefined;
      if (!saved?.webhookUrl) throw new Error("The rotated webhook URL was not returned.");
      setCredential({ name: saved.name, webhookUrl: saved.webhookUrl });
      setMessage("Credential rotated. Give this new webhook URL to the provider.");
      await load();
    } catch (cause) { setErrorMessage(cause instanceof Error ? cause.message : "The credential could not be rotated."); }
    finally { setBusyId(null); }
  }

  async function remove(item: RestockProvider) {
    if (!window.confirm(`Delete “${item.name}”? Its webhook will immediately stop working.`)) return;
    try {
      setBusyId(item.id);
      await request(`/api/v1/admin/in-store-restock-providers/${encodeURIComponent(item.id)}`, { method: "DELETE" });
      if (credential?.name === item.name) setCredential(null);
      await load();
    } catch (cause) { setErrorMessage(cause instanceof Error ? cause.message : "The provider could not be deleted."); }
    finally { setBusyId(null); }
  }

  async function copy(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setMessage("Webhook URL copied.");
    } catch { setErrorMessage("Copy failed. Select the URL and copy it manually."); }
  }

  return <div className="admin-stack">
    <Panel title={editing ? `Edit provider: ${editing.name}` : "Create restock provider"} action={<Badge tone="gold">50-mile alerts</Badge>}>
      <div className="source-intro"><p>Create a dedicated credential for each in-store restock provider. The complete URL appears only after creation or rotation.</p></div>
      <form className="source-form" onSubmit={(event) => void save(event)}>
        <div className="form-columns"><Field label="Provider name"><input required value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Restock provider" /></Field><Field label="URL slug"><input required value={form.slug} onChange={(event) => update("slug", event.target.value)} placeholder="restock-provider" /></Field></div>
        <div className="form-columns"><Field label="Retailer (optional)"><input value={form.retailer} onChange={(event) => update("retailer", event.target.value)} placeholder="Target" /></Field><Field label="Region"><input value={form.region} onChange={(event) => update("region", event.target.value)} placeholder="US" /></Field></div>
        <Toggle checked={form.enabled} onChange={(value) => update("enabled", value)} label="Accept incoming restocks" />
        <div className="source-form-actions">{editing && <Button type="button" variant="ghost" onClick={resetForm}>Cancel</Button>}<Button type="submit" disabled={busyId === (editing?.id || "new")}>{busyId ? "Saving…" : editing ? "Save provider" : "Create webhook"}</Button></div>
      </form>
    </Panel>
    {credential && <Panel title="Copy this webhook now" action={<Badge tone="gold">Shown once</Badge>}><p className="panel-note">Give this full URL to {credential.name}. Treat it like a password; it will not appear in the provider list.</p><label className="source-url"><span>Provider webhook URL</span><input readOnly value={credential.webhookUrl} onFocus={(event) => event.currentTarget.select()} /></label><div className="source-actions"><Button onClick={() => void copy(credential.webhookUrl)}>Copy webhook</Button><Button variant="ghost" onClick={() => setCredential(null)}>Dismiss</Button></div></Panel>}
    <Panel title="In-store restock providers" action={<Button variant="ghost" onClick={() => void load()}>Refresh</Button>}>
      {message && <p className="success-note content-message">✓ {message}</p>}
      {errorMessage && <p className="error-note content-message">! {errorMessage}</p>}
      {loading && <p className="loading-content">Loading providers…</p>}
      {!loading && items.length === 0 && <div className="empty-content"><strong>No providers yet</strong><p>Create one above to generate a secure webhook.</p></div>}
      <div className="source-list">{items.map((item) => <article className="source-card" key={item.id}>
        <div className="source-card-header"><div><div className="content-badges"><Badge tone={item.enabled ? "green" : "muted"}>{item.enabled ? "Enabled" : "Disabled"}</Badge>{item.region && <Badge tone="blue">{item.region}</Badge>}</div><h3>{item.name}</h3><p>{item.retailer || "All retailers"}</p></div><small>Updated {formatTimestamp(item.updatedAt)}</small></div>
        <label className="source-url"><span>Endpoint (credential hidden)</span><input readOnly value={item.endpoint} onFocus={(event) => event.currentTarget.select()} /></label>
        <div className="source-actions"><Button variant="ghost" disabled={busyId === item.id} onClick={() => void rotate(item)}>Rotate credential</Button><Button variant="ghost" disabled={busyId === item.id} onClick={() => beginEdit(item)}>Edit</Button><Button variant={item.enabled ? "ghost" : "green"} disabled={busyId === item.id} onClick={() => void toggle(item)}>{item.enabled ? "Disable" : "Enable"}</Button><button className="danger-button" disabled={busyId === item.id} onClick={() => void remove(item)}>Delete</button></div>
        <div className="source-status"><span>Last received: {item.lastSeenAt ? formatTimestamp(item.lastSeenAt) : "Never"}</span><span>Status: {item.lastStatus || "Waiting"}</span>{item.lastError && <span className="source-error">{item.lastError}</span>}</div>
      </article>)}</div>
    </Panel>
  </div>;
}

function Overview() {
  const { tcgs } = useAdminTcgs();
  return <div className="admin-stack"><section className="admin-metrics"><Panel><small>Authentication</small><h2>Clerk</h2><span>Administrator verified</span></Panel><Panel><small>Content storage</small><h2>D1</h2><span>Full content controls active</span></Panel><Panel><small>TCGs offered</small><h2>{tcgs.filter((tcg) => tcg.enabled).length}</h2><span>Manual updates supported</span></Panel><Panel><small>Notifications</small><h2>Queue</h2><span>Newly published posts are queued</span></Panel></section><Panel title="Content operations are live"><p className="panel-note">Open Content to manage existing posts, Publish to create one, TCGs to extend the catalog, or Alert sources to connect provider webhooks. Every change is protected by Clerk and recorded in the D1 audit log.</p></Panel></div>;
}

function ComingSoon({ title }: { title: string }) {
  return <Panel title={title}><p className="panel-note">This control will be connected in a later milestone. Content publishing and management are active now.</p></Panel>;
}


interface ChatReport { id:string; message_id:string; reason:string; details:string|null; status:string; reporter_name:string|null; created_at:string; }
function ChatModeration() {
  const { getToken }=useAuth(); const [reports,setReports]=useState<ChatReport[]>([]); const [error,setError]=useState("");
  const load=useCallback(async()=>{try{const token=await getToken();const response=await fetch(`${apiUrl}/api/v1/admin/chat/reports`,{headers:{authorization:`Bearer ${token}`}});const payload=await response.json() as any;if(!response.ok)throw new Error(payload.error?.message||"Unable to load reports.");setReports(payload.data||[]);setError("");}catch(cause){setError(cause instanceof Error?cause.message:"Unable to load reports.");}},[getToken]);
  useEffect(()=>{void load();},[load]);
  async function resolve(report:ChatReport,status:"resolved"|"dismissed"){const token=await getToken();await fetch(`${apiUrl}/api/v1/admin/chat/reports/${encodeURIComponent(report.id)}`,{method:"PUT",headers:{authorization:`Bearer ${token}`,"content-type":"application/json"},body:JSON.stringify({status})});await load();}
  async function remove(report:ChatReport){const token=await getToken();await fetch(`${apiUrl}/api/v1/chat/messages/${encodeURIComponent(report.message_id)}`,{method:"DELETE",headers:{authorization:`Bearer ${token}`}});await resolve(report,"resolved");}
  return <div className="admin-stack"><Panel title="Chat moderation"><p className="panel-note">Review reports from the single members chat. Deletions and resolutions are recorded in D1.</p>{error&&<p>{error}</p>}<div className="admin-stack">{reports.map(report=><article className="admin-form" key={report.id}><div><Badge tone={report.status==="open"?"gold":"muted"}>{report.status}</Badge><h3>{report.reason}</h3><p>{report.details||"No additional details."}</p><small>Reported by {report.reporter_name||"member"} · {new Date(report.created_at).toLocaleString()}</small></div><div className="admin-actions"><Button onClick={()=>void remove(report)}>Delete message & resolve</Button><Button variant="ghost" onClick={()=>void resolve(report,"resolved")}>Resolve</Button><Button variant="ghost" onClick={()=>void resolve(report,"dismissed")}>Dismiss</Button></div></article>)}</div>{!reports.length&&!error&&<p className="panel-note">No reports.</p>}</Panel></div>;
}

function AdminApp() {
  const [tab, setTab] = useState<AdminTab>("Content");
  return <main className="admin-app"><aside><BrandLogo src={logo} /><Badge tone="gold">Admin</Badge><nav>{nav.map((item) => <button className={tab === item ? "active" : ""} onClick={() => setTab(item)} key={item}>{item}</button>)}</nav><div className="admin-user"><UserButton /><div><strong>DropDex Staff</strong><small>Administrator</small></div></div></aside><section className="admin-main"><header><div><small>PRIVATE CONTROL PLANE</small><h1>{tab}</h1></div><Badge tone="green">Secure API</Badge></header><div className="admin-content">{tab === "Content" ? <ContentManager /> : tab === "Publish" ? <Publisher /> : tab === "TCGs" ? <TcgManager /> : tab === "Alert sources" ? <AlertSources /> : tab === "In-Store Restocks" ? <RestockProviders /> : tab === "Chat moderation" ? <ChatModeration /> : tab === "Overview" ? <Overview /> : <ComingSoon title={tab} />}</div></section></main>;
}

function AdminGate() {
  const { getToken } = useAuth();
  const [access, setAccess] = useState<"checking" | "allowed" | "denied" | "error">("checking");

  useEffect(() => {
    let active = true;
    async function verify() {
      try {
        const token = await getToken();
        if (!token || !apiUrl) throw new Error("Missing admin API configuration");
        const response = await fetch(`${apiUrl}/api/v1/me`, { headers: { authorization: `Bearer ${token}` } });
        if (!response.ok) throw new Error("Unable to verify the administrator session");
        const payload = await response.json() as { data?: { isAdmin?: boolean } };
        if (active) setAccess(payload.data?.isAdmin ? "allowed" : "denied");
      } catch {
        if (active) setAccess("error");
      }
    }
    void verify();
    return () => { active = false; };
  }, [getToken]);

  if (access === "allowed") return <AdminApp />;
  return <main className="admin-access"><BrandLogo src={logo} /><Badge tone={access === "checking" ? "muted" : "gold"}>{access === "checking" ? "Checking access" : "Protected"}</Badge><h1>{access === "checking" ? "Verifying administrator…" : access === "denied" ? "Administrator access required" : "Admin API unavailable"}</h1><p>{access === "denied" ? "This signed-in DropDex account is not included in ADMIN_CLERK_USER_IDS." : access === "error" ? "Confirm VITE_API_URL and the Worker's authorized origins, then try again." : "Your Clerk session is being verified by the DropDex API."}</p>{access !== "checking" && <UserButton />}</main>;
}

function Root() {
  if (!clerkPublishableKey) return <main className="admin-access"><BrandLogo src={logo} /><Badge tone="gold">Configuration required</Badge><h1>Admin authentication is not configured</h1><p>Add VITE_CLERK_PUBLISHABLE_KEY and VITE_API_URL to the admin deployment.</p></main>;
  return <ClerkProvider publishableKey={clerkPublishableKey}><Show when="signed-in" fallback={<main className="admin-sign-in"><BrandLogo src={logo} /><SignIn routing="hash" fallbackRedirectUrl="/" /></main>}><AdminGate /></Show></ClerkProvider>;
}

createRoot(document.getElementById("root")!).render(<StrictMode><Root /></StrictMode>);
